import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import SEO from "../components/seo/SEO";
import { useLanguage } from "../i18n/LanguageContext";
import {
  SEO_DATA,
  SERVICE_KEYS,
  PAGE_COPY,
  pathFor,
} from "../i18n/seoMeta";

// Landing page for /sluzby/ (CZ) and /en/services/ (EN). Lists all six
// services as cards linking to their detail pages. SEO-friendly H1 + intro.
export const ServicesIndexPage = () => {
  const { lang } = useLanguage();
  const meta = SEO_DATA[lang].services;
  const copy = PAGE_COPY[lang].servicesIndex;
  const homePath = pathFor("home", lang);

  return (
    <div data-testid="services-index-page" className="bg-[#181B24] text-white">
      <SEO routeId="services" lang={lang} />
      <Header />
      <main>
        <section className="bg-[#202535] pt-36 md:pt-44 pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
            <nav
              data-testid="services-breadcrumb"
              className="font-body text-sm text-white/60 mb-6 flex items-center gap-2"
            >
              <Link to={homePath} className="hover:text-[#FFE21A] transition-colors">
                {lang === "cs" ? "Domů" : "Home"}
              </Link>
              <ChevronRight size={14} />
              <span className="text-white/90">{meta.h1}</span>
            </nav>

            <h1
              data-testid="services-h1"
              className="font-heading uppercase font-bold leading-[1.02] tracking-[0.01em] text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
            >
              {meta.h1.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="gradient-text">
                {meta.h1.split(" ").slice(-1)[0]}
              </span>
            </h1>
            <p
              data-testid="services-lead"
              className="mt-6 max-w-3xl font-body text-white/80 text-base md:text-lg leading-relaxed"
            >
              {copy.lead}
            </p>
          </div>
        </section>

        <section className="bg-[#F3F4F5] text-[#181B24] py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {SERVICE_KEYS.map((id, idx) => {
                const sm = SEO_DATA[lang][id];
                const path = pathFor(id, lang);
                return (
                  <Link
                    key={id}
                    to={path}
                    data-testid={`service-card-${id}`}
                    className="group relative bg-white rounded-2xl p-7 md:p-8 ring-1 ring-[#181B24]/10 shadow-[0_15px_40px_rgba(24,27,36,0.06)] hover-lift transition-all"
                  >
                    <div className="font-heading font-bold text-[#FFE21A] text-5xl md:text-6xl mb-4 leading-none">
                      0{idx + 1}
                    </div>
                    <h2 className="font-heading font-bold uppercase tracking-[0.02em] text-[#181B24] text-xl md:text-2xl leading-tight mb-3">
                      {sm.h1}
                    </h2>
                    <p className="font-body text-[#181B24]/75 text-sm md:text-base leading-relaxed mb-6">
                      {sm.description}
                    </p>
                    <div className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.15em] text-xs md:text-sm text-[#181B24] group-hover:text-[#FFE21A] transition-colors">
                      {copy.readMore}
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesIndexPage;
